import React from 'react';
import TitleHeader from '../../atoms/TitleHeader/TitleHeader';
import consultingBg from '../../../assets/images/servicepage/consulting.webp';
import Constants from '../../../app/utils/constants';
import colors from '../../theme/color';

import { motion } from 'framer-motion';

const ConsultingSection = () => {
  const consulting = Constants.servicePage.consulting;
  const titleBadgeBorderClassName = 'bg-service-title-badge';
  const sectionHeadingClassName =
    'text-center font-rajdhani text-[40px] font-semibold text-white md:text-[48px] lg:text-[50px] xl:text-[56px] min-[2560px]:text-[82px]';

  return (
    <section className="relative z-10 w-full overflow-hidden px-6 py-10 sm:px-10 sm:py-10 lg:px-8 lg:py-12 xl:px-32 xl:py-15 min-[2560px]:px-44 min-[2560px]:py-20">
      <div className="relative mx-auto max-w-[1200px] min-[2560px]:max-w-[2200px]">

        {/* HEADER */}
        <div className="relative z-10 mb-12 flex flex-col items-center text-center min-[2560px]:mb-18">
           
          <div className="relative mb-6">
            <TitleHeader
              badgeText={consulting.badgeText}
              titleLines={consulting.titleLines}
              wrapperClassName="flex flex-col items-center"
              titleClassName={sectionHeadingClassName}
              badgeClassName="px-10 py-3 text-2xl md:px-12 md:py-4 md:text-3xl min-[1440px]:px-14 min-[1440px]:text-[2rem]"
              badgeBorderClassName={titleBadgeBorderClassName}
            />
           
          </div>
        </div>


        {/* ADVISORY CARDS */}
        <div className="relative z-10 mb-20 grid gap-6 lg:grid-cols-3 min-[2560px]:mb-28 min-[2560px]:gap-10">
          <div
            className="pointer-events-none absolute left-1/2 -top-16 -bottom-6 -z-10 w-screen -translate-x-1/2 bg-cover bg-center bg-no-repeat opacity-90 lg:-top-20 lg:-bottom-8 min-[2560px]:-top-24 min-[2560px]:-bottom-12"
            style={{ backgroundImage: `url(${consultingBg})` }}
          />

          {consulting.advisoryCards.map((card, index) => {
            return (
              <motion.div
                key={card.title.join(' ')}
                className="group rounded-[20px] bg-purple/12 p-[1.5px] transition-colors duration-1000 ease-in-out hover:bg-service-consulting-center-border min-[2560px]:rounded-[28px] min-[2560px]:p-[2px]"
                initial={{ x: -80, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: false, amount: 0.2 }}
                transition={{ duration: 0.55, delay: index * 0.1, ease: 'easeOut' }}
              >
                <article
                  className="relative h-full overflow-hidden rounded-[20px] bg-service-consulting-card p-6 transition-colors duration-1000 ease-in-out group-hover:bg-service-consulting-card-center min-[2560px]:rounded-[27px] min-[2560px]:p-12"
                >
                  <span
                    className="pointer-events-none absolute -left-18 -top-18 h-60 w-60 rounded-full opacity-0 blur-3xl transition-opacity duration-1400 ease-in-out group-hover:opacity-100 min-[2560px]:-left-24 min-[2560px]:-top-24 min-[2560px]:h-84 min-[2560px]:w-84"
                    style={{ background: colors.serviceConsultingCenterGlow }}
                  />
                  <span
                    className="pointer-events-none absolute left-0 top-0 h-40 w-52 opacity-0 transition-opacity duration-1400 ease-in-out group-hover:opacity-100 bg-service-consulting-radial-center"
                  />

                  <h3
                    className="relative z-10 mb-4 bg-service-consulting-heading bg-clip-text font-gilory-semibold text-[28px] font-bold leading-[1.10] text-transparent min-[2560px]:mb-8 min-[2560px]:text-[52px]"
                  >
                    {card.title[0]} <br /> {card.title[1]}
                  </h3>

                  <p
                    className="relative z-10 text-[15px] leading-[1.6] min-[2560px]:text-[28px] min-[2560px]:leading-[1.55]"
                    style={{ color: colors.serviceConsultingBody }}
                  >
                    {card.body}
                  </p>
                </article>
              </motion.div>
            );
          })}
        </div>

        {/* ENGAGEMENT MODELS */}
        <div className="relative z-10">
          <h3 className={`mb-4 ${sectionHeadingClassName}`}>
            {consulting.engagementHeading}
          </h3>


          <div className="grid items-stretch gap-6 lg:grid-cols-2 min-[2560px]:gap-10">
            {consulting.engagementCards.map((card, index) => (
              <motion.div
                key={card.title}
                className="relative h-full overflow-hidden rounded-[20px] min-[2560px]:rounded-[28px]"
                initial={{ x: 80, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: false, amount: 0.2 }}
                transition={{ duration: 0.55, delay: index * 0.12, ease: 'easeOut' }}
              >
                <span
                  className="pointer-events-none absolute inset-0 z-20 rounded-[20px] min-[2560px]:rounded-[28px]"
                  style={{
                    padding: '1.5px',
                    background: 'radial-gradient(105% 80% at 100% 0%, #744DFF 10%, transparent 100%)',
                    WebkitMask:
                      'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  }}
                />

                <div className="relative z-10 h-full overflow-hidden rounded-[20px] bg-service-engagement-card-inner p-8 min-[2560px]:rounded-[27px] min-[2560px]:p-12">
                  <span
                    className="pointer-events-none absolute -bottom-20 -left-40 h-60 w-120 rounded-full blur-3xl min-[2560px]:-bottom-26 min-[2560px]:-left-26 min-[2560px]:h-84 min-[2560px]:w-84"
                    style={{ background: colors.serviceConsultingInnerGlow }}
                  />
                  <span
                    className="pointer-events-none absolute bottom-0 left-0 h-36 w-56 bg-service-consulting-radial-inner"
                  />

                  <h4 className="relative z-10 mb-3 font-rajdhani text-[24px] font-bold text-purple min-[2560px]:mb-6 min-[2560px]:text-[42px]">
                    {card.title}
                  </h4>

                  <p
                    className="relative z-10 text-[15px] leading-[1.6] min-[2560px]:text-[26px] min-[2560px]:leading-[1.55]"
                    style={{ color: colors.serviceConsultingBody }}
                  >
                    {card.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default ConsultingSection;
